import React, { Suspense } from 'react';

import { PageError } from '@/components/Error';
import useRestBlocks from '@/hooks/useRestBlocks';
import {
  CourseContext,
  CourseContextProvider,
} from '@/modules/corporate/modules/course/courseContext';
import { useFetchCourseQuery } from '@/modules/corporate/modules/course/graphql/queries/generated/course';
import useCourseActions from '@/modules/corporate/modules/course/hooks/useCourseActions';
import { getEmptyIndex, getNumberIndex } from '@/modules/corporate/modules/course/utils/getIndex';
import { showNumberIndex } from '@/modules/corporate/modules/course/views/CourseBuilder/const';
import { CourseContentType } from '@/types/schema';

import LessonContent from './content';

function LessonViewer({
  id,
  name,
  parentId,
  parentName,
}: {
  id: string;
  name: string;
  parentId: string;
  parentName?: string;
}) {
  const lesson = {
    id: id,
    name: name,
    description: parentName || 'Lesson',
  };
  const courseId = parentId;

  const [{ data: courseData, error: errorCourse, fetching: fetchingCourse }] = useFetchCourseQuery({
    variables: { courseId },
  });
  const courseRootBlockId = (courseData?.content as CourseContentType)?.rootBlockId;
  const {
    data: courseBlocks,
    error: errorBlocks,
    loading: fetchingBlocks,
  } = useRestBlocks(courseRootBlockId, courseId);
  const {
    data: lessonBlocks,
    error,
    loading: fetchingLessonBlocks,
  } = useRestBlocks(lesson.id, courseId);

  const fetching = fetchingLessonBlocks || fetchingCourse || fetchingBlocks;

  const {
    currentLesson,
    lessons,
    onAction,
    rootLessons,
    lessonsParent,
    importDialogOpen,
    allLessonBasicInfo,
  } = useCourseActions(courseId, lesson.id!);

  const contextValue: CourseContext = React.useMemo(
    () => ({
      allLessonBasicInfo,
      course: courseData?.content as CourseContentType,
      courseContent: courseBlocks?.content!,
      currentLesson,
      getSidebarIndex: showNumberIndex ? getNumberIndex : getEmptyIndex,
      importDialogOpen,
      lessonPageId: lesson.id!,
      lessons,
      lessonsParent,
      rootLessons,
      onAction,
    }),
    [
      allLessonBasicInfo,
      courseBlocks?.content,
      courseData?.content,
      currentLesson,
      importDialogOpen,
      lesson.id,
      lessons,
      lessonsParent,
      onAction,
      rootLessons,
    ],
  );

  if (error || errorCourse || errorBlocks) {
    return <PageError error={error || errorCourse || errorBlocks} />;
  }

  return (
    <Suspense fallback={null}>
      <CourseContextProvider {...contextValue}>
        <LessonContent data={lessonBlocks} fetching={fetching} lesson={lesson} />
      </CourseContextProvider>
    </Suspense>
  );
}

export default LessonViewer;
